import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter, ListChecks, Plus, Search, X } from "lucide-react";

import { useCreateMaterialList, useMaterialLists } from "@/api/queries";
import type {
  MaterialList,
  MaterialListStatus,
  MaterialListType,
} from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDate,
  formatMaterialListStatus,
  formatMaterialListType,
  pluralize,
} from "@/lib/format";
import { materialListTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const TYPES: MaterialListType[] = [
  "PURCHASE",
  "PROJECT_QUOTE",
  "REPLENISHMENT",
  "PREPARATION",
  "GENERAL",
];

const STATUSES: MaterialListStatus[] = [
  "DRAFT",
  "ACTIVE",
  "COMPLETED",
  "ARCHIVED",
];

/**
 * The lists (V1.6-01).
 *
 * A list is what somebody asked for, before HD-Vault agrees to anything. No
 * stock figure appears on this screen and none belongs here: a list does not
 * reserve, does not buy, and does not know what is on the shelf. Showing an
 * availability column would suggest it does.
 */
export function MaterialListsPage() {
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(term);
  const [creating, setCreating] = useState(false);

  // Ouvrir la création depuis un lien — le dashboard s'en sert. Le paramètre
  // est consommé tout de suite pour que revenir en arrière ne rouvre pas la
  // boîte, et pour qu'un rafraîchissement ne la rouvre pas non plus.
  useEffect(() => {
    if (params.get("new") === null) return;
    setCreating(true);
    const merged = new URLSearchParams(params);
    merged.delete("new");
    setParams(merged, { replace: true });
  }, [params, setParams]);


  const type = params.get("type") ?? undefined;
  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [term]);

  const query = useMemo(
    () => ({
      search: debounced || undefined,
      type: type as MaterialListType | undefined,
      status: status as MaterialListStatus | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [debounced, type, status, offset],
  );

  const { data, isLoading, error, refetch } = useMaterialLists(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  const hasFilters = Boolean(debounced || type || status);

  return (
    <Page>
      <PageHeader
        title="Listes"
        subtitle="Ce que quelqu'un a demandé. Pas encore un besoin, pas encore une commande."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Nouvelle liste
          </Button>
        }
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                update({ q: event.target.value, offset: undefined });
              }}
              placeholder="Nom, code…"
              className="pl-9"
              aria-label="Rechercher une liste"
            />
          </div>

          <Select
            value={type ?? ""}
            onChange={(event) =>
              update({ type: event.target.value || undefined })
            }
            aria-label="Filtrer par type"
            className="sm:w-44"
          >
            <option value="">Tous les types</option>
            {TYPES.map((value) => (
              <option key={value} value={value}>
                {formatMaterialListType(value)}
              </option>
            ))}
          </Select>

          <Select
            value={status ?? ""}
            onChange={(event) =>
              update({ status: event.target.value || undefined })
            }
            aria-label="Filtrer par statut"
            className="sm:w-44"
          >
            <option value="">Statuts actifs</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatMaterialListStatus(value)}
              </option>
            ))}
          </Select>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setTerm("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={5} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                hasFilters ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <ListChecks className="size-6" aria-hidden />
                )
              }
              title={hasFilters ? "Aucune liste ne correspond" : "Aucune liste"}
              description={
                hasFilters
                  ? "Essayez un autre terme, ou retirez les filtres."
                  : "Une liste sert à noter ce qu'il faut, même quand l'article n'existe pas encore au catalogue."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-32 whitespace-nowrap">Code</Th>
                      <Th className="max-w-0">Liste</Th>
                      <Th className="hidden whitespace-nowrap @3xl:table-cell">
                        Type
                      </Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">
                        Créée
                      </Th>
                      <Th align="right" className="whitespace-nowrap">
                        Lignes
                      </Th>
                      <Th>Statut</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <ListRow key={item.id} item={item} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <ListCard item={item} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <NewListDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

function ListRow({ item }: { item: MaterialList }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/lists/${item.id}`} className="hover:text-brand">
          {item.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link
          to={`/lists/${item.id}`}
          className="block min-w-0 hover:text-brand"
        >
          <span className="block truncate font-medium text-ink">
            {item.name}
          </span>
          {item.description && (
            <span className="block truncate text-[11px] text-ink-subtle">
              {item.description}
            </span>
          )}
        </Link>
      </Td>
      <Td className="hidden whitespace-nowrap text-[13px] text-ink-muted @3xl:table-cell">
        {formatMaterialListType(item.list_type)}
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @4xl:table-cell">
        {formatDate(item.created_at)}
      </Td>
      <Td
        align="right"
        className="tnum whitespace-nowrap text-[13px] text-ink-muted"
      >
        {item.line_count}
      </Td>
      <Td>
        <StatusBadge tone={materialListTone(item.status)}>
          {formatMaterialListStatus(item.status)}
        </StatusBadge>
      </Td>
    </Tr>
  );
}

function ListCard({ item }: { item: MaterialList }) {
  return (
    <Link
      to={`/lists/${item.id}`}
      className="flex min-h-[64px] items-center gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{item.name}</p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">
          {item.code}
        </p>
        <p className="mt-0.5 truncate text-[12px] text-ink-muted">
          {formatMaterialListType(item.list_type)} ·{" "}
          {pluralize(item.line_count, "ligne")}
        </p>
      </div>
      <StatusBadge tone={materialListTone(item.status)} className="shrink-0">
        {formatMaterialListStatus(item.status)}
      </StatusBadge>
    </Link>
  );
}

function NewListDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const createList = useCreateMaterialList();
  const [name, setName] = useState("");
  const [listType, setListType] = useState<MaterialListType>("GENERAL");
  const [description, setDescription] = useState("");

  function reset() {
    setName("");
    setListType("GENERAL");
    setDescription("");
  }

  async function submit() {
    if (!name.trim()) return;
    try {
      const created = await createList.mutateAsync({
        name,
        list_type: listType,
        description,
      });
      toast.success("Liste créée", `${created.code} — ${created.name}`);
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Liste non créée", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Nouvelle liste"
      description="HD-Vault attribue le code. Le reste, c'est vos mots."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!name.trim() || createList.isPending}
          >
            {createList.isPending ? "Création…" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Nom de la liste">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Materiel pour lundi"
            aria-label="Nom de la liste"
          />
        </Field>
        <Field label="Type">
          <Select
            value={listType}
            onChange={(event) =>
              setListType(event.target.value as MaterialListType)
            }
            aria-label="Type de liste"
          >
            {TYPES.map((value) => (
              <option key={value} value={value}>
                {formatMaterialListType(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Description" hint="Facultatif.">
          <Input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            aria-label="Description de la liste"
          />
        </Field>
      </div>
    </Dialog>
  );
}
